import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Plus, FileText, Edit2, Trash2, ExternalLink, Eye, EyeOff, Save, X, GripVertical,
  ArrowLeft, Loader2, AlertCircle, CheckCircle2, Globe, FileEdit, Link2,
} from 'lucide-react';
import AiGenerateButton from '@/components/ai/AiGenerateButton';
import { invalidateMenuCache } from '@/lib/useMenu';

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const slugify = (s) => (s || '').toString().toLowerCase().trim()
  .replace(/[^a-z0-9\s-_]/g, '')
  .replace(/[\s_]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-+|-+$/g, '');

export default function PagesAndMenu() {
  const [view, setView] = useState('pages'); // 'pages' | 'menu'

  return (
    <div className="space-y-6" data-testid="pages-and-menu">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-gold font-semibold">Pages & Menu</div>
          <h2 className="font-display uppercase text-3xl mt-1">Build your site</h2>
          <p className="text-sm text-muted-kc mt-1">Create custom pages and wire them into your header / footer navigation.</p>
        </div>
      </div>

      <div className="flex gap-1 bg-surface-2 p-1 rounded-full w-fit border border-kc" data-testid="pm-subtabs">
        <SubTab active={view === 'pages'} onClick={() => setView('pages')} icon={FileText} label="Pages" testid="pm-tab-pages"/>
        <SubTab active={view === 'menu'} onClick={() => setView('menu')} icon={Link2} label="Menu items" testid="pm-tab-menu"/>
      </div>

      {view === 'pages' && <PageManager />}
      {view === 'menu' && <MenuManager />}
    </div>
  );
}

const SubTab = ({ active, onClick, icon: Icon, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 transition-colors ${
      active ? 'bg-ink text-white' : 'text-muted-kc hover:bg-white'
    }`}
  >
    <Icon size={13}/> {label}
  </button>
);

/* ============================================================
   PAGE MANAGER
   ============================================================ */
function PageManager() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // page object being edited or 'new'
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/pages');
      setPages(r.data?.items || []);
    } catch (e) {
      toast.error('Could not load pages', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreateClick = () => {
    setEditing({
      id: null, title: '', slug: '', body: '', cover_image: '',
      cta_label: '', cta_href: '', meta_title: '', meta_description: '', published: false,
    });
    setCreating(true);
  };

  const onSave = async (formData) => {
    try {
      if (creating || !formData.id) {
        const r = await api.post('/admin/pages', formData);
        toast.success('Page created.', { description: `/p/${r.data.slug}` });
      } else {
        await api.put(`/admin/pages/${formData.id}`, formData);
        toast.success('Page saved.');
      }
      setEditing(null); setCreating(false);
      load();
      invalidateMenuCache();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error('Save failed', { description: String(msg).slice(0, 220) });
    }
  };

  const onDelete = async (page) => {
    if (!window.confirm(`Delete page "${page.title}"?\n\nThis will also remove any menu items pointing to it. This can’t be undone.`)) return;
    try {
      await api.delete(`/admin/pages/${page.id}`);
      toast.success('Page deleted.');
      load();
      invalidateMenuCache();
    } catch (e) {
      toast.error('Delete failed', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    }
  };

  const onTogglePublished = async (page) => {
    try {
      await api.put(`/admin/pages/${page.id}`, { published: !page.published });
      toast.success(page.published ? 'Unpublished.' : 'Published.');
      load();
    } catch (e) {
      toast.error('Could not toggle', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    }
  };

  if (editing) {
    return (
      <PageEditor
        page={editing}
        isNew={creating}
        onCancel={() => { setEditing(null); setCreating(false); }}
        onSave={onSave}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-muted-kc">{loading ? 'Loading…' : `${pages.length} page${pages.length === 1 ? '' : 's'}`}</div>
        <button
          type="button"
          onClick={onCreateClick}
          className="h-10 px-4 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors"
          data-testid="page-create"
        >
          <Plus size={16}/> New page
        </button>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-muted-kc" size={20}/></div>
      ) : pages.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-kc-strong rounded-[var(--kc-radius-md)] bg-surface-2">
          <FileText size={32} className="mx-auto text-muted-kc mb-3"/>
          <div className="font-display uppercase text-lg mb-1">No pages yet</div>
          <div className="text-sm text-muted-kc">Click <strong>New page</strong> to create your first custom page.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="pages-list">
          {pages.map((p) => (
            <PageCard
              key={p.id}
              page={p}
              onEdit={() => setEditing(p)}
              onDelete={() => onDelete(p)}
              onTogglePublished={() => onTogglePublished(p)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PageCard({ page, onEdit, onDelete, onTogglePublished }) {
  return (
    <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4 hover:border-[color:var(--kc-gold)] transition-colors" data-testid={`page-card-${page.slug}`}>
      <div className="flex items-start gap-3">
        {page.cover_image ? (
          <img src={page.cover_image.startsWith('http') || page.cover_image.startsWith('/splash') ? page.cover_image : `${process.env.REACT_APP_BACKEND_URL || ''}${page.cover_image}`} alt="" className="w-14 h-14 rounded-[var(--kc-radius-sm)] object-cover border border-kc flex-shrink-0"/>
        ) : (
          <div className="w-14 h-14 rounded-[var(--kc-radius-sm)] bg-surface-2 border border-kc inline-flex items-center justify-center flex-shrink-0"><FileText size={20} className="text-muted-kc"/></div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display uppercase text-lg leading-tight truncate">{page.title}</h3>
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${page.published ? 'bg-[color:var(--kc-success)]/15 text-[color:var(--kc-success)]' : 'bg-surface-2 text-muted-kc'}`}>
              {page.published ? 'Published' : 'Draft'}
            </span>
          </div>
          <div className="mt-0.5 text-xs text-muted-kc font-mono">/p/{page.slug}</div>
          {page.meta_description && <p className="mt-1.5 text-xs text-muted-kc line-clamp-2">{page.meta_description}</p>}
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-kc flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <button onClick={onTogglePublished} className="h-8 px-2 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold text-muted-kc hover:bg-surface-2 hover:text-ink transition-colors" data-testid={`page-publish-${page.slug}`}>
            {page.published ? <><EyeOff size={12}/> Unpublish</> : <><Eye size={12}/> Publish</>}
          </button>
          {page.published && (
            <a href={`/p/${page.slug}`} target="_blank" rel="noreferrer" className="h-8 px-2 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold text-muted-kc hover:bg-surface-2 hover:text-ink transition-colors" data-testid={`page-view-${page.slug}`}>
              <ExternalLink size={12}/> View
            </a>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onEdit} className="h-8 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold bg-ink text-white hover:bg-black/90 transition-colors" data-testid={`page-edit-${page.slug}`}>
            <Edit2 size={12}/> Edit
          </button>
          <button onClick={onDelete} className="h-8 px-2 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold text-[color:var(--kc-danger)] hover:bg-red-50 transition-colors" data-testid={`page-delete-${page.slug}`}>
            <Trash2 size={12}/>
          </button>
        </div>
      </div>
    </div>
  );
}

function PageEditor({ page, isNew, onCancel, onSave }) {
  const [form, setForm] = useState(page);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const coverInputRef = useRef(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onTitleChange = (v) => {
    setForm((f) => ({ ...f, title: v, slug: slugTouched ? f.slug : slugify(v) }));
  };

  const validation = useMemo(() => {
    const errs = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    if (!form.slug.trim()) errs.slug = 'Slug is required';
    else if (!SLUG_RE.test(form.slug)) errs.slug = 'Use lowercase letters, numbers, and dashes only';
    if (form.cta_label && !form.cta_href) errs.cta_href = 'Add a link for the CTA, or clear the label';
    return errs;
  }, [form]);
  const canSave = Object.keys(validation).length === 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const uploadCover = async (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error('Image too large', { description: 'Max 12 MB.' });
      return;
    }
    setUploading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('purpose', 'page-cover');
    try {
      const r = await api.post('/admin/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setField('cover_image', r.data.url);
      toast.success('Cover uploaded.');
    } catch (e) {
      toast.error('Upload failed', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    } finally { setUploading(false); if (coverInputRef.current) coverInputRef.current.value = ''; }
  };

  const coverSrc = form.cover_image && !form.cover_image.startsWith('http') && !form.cover_image.startsWith('/splash')
    ? `${process.env.REACT_APP_BACKEND_URL || ''}${form.cover_image}`
    : form.cover_image;

  return (
    <div className="space-y-4" data-testid="page-editor">
      <div className="flex items-center justify-between gap-3 bg-white border border-kc rounded-[var(--kc-radius-md)] p-3 sticky top-2 z-10 shadow-sm">
        <button onClick={onCancel} className="h-9 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm font-semibold text-muted-kc hover:bg-surface-2 transition-colors" data-testid="page-back">
          <ArrowLeft size={14}/> Back
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-2 text-sm font-semibold cursor-pointer select-none">
            <input type="checkbox" checked={!!form.published} onChange={(e) => setField('published', e.target.checked)} className="kc-checkbox" data-testid="page-published-toggle"/>
            {form.published ? <span className="text-[color:var(--kc-success)]">Published</span> : <span className="text-muted-kc">Draft</span>}
          </label>
          <button
            onClick={handleSave}
            disabled={!canSave || saving}
            className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="page-save"
          >
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4 space-y-3">
            <FieldHeader label="Title" required ai={{ field: 'page_title', value: form.title, onApply: (t) => onTitleChange(t), length: 'short', page: 'generic' }}/>
            <input value={form.title} onChange={(e) => onTitleChange(e.target.value)} className="kc-tinput text-lg font-display" placeholder="My new page" data-testid="page-title"/>
            {validation.title && <Hint type="error">{validation.title}</Hint>}

            <FieldHeader label="URL slug" required hint="The address this page lives at"/>
            <div className="flex items-center gap-2 bg-surface-2 border border-kc rounded-[var(--kc-radius-sm)] px-3 py-2">
              <span className="text-sm text-muted-kc font-mono">/p/</span>
              <input
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); setField('slug', slugify(e.target.value)); }}
                onBlur={() => setSlugTouched(true)}
                className="flex-1 bg-transparent outline-none text-sm font-mono"
                placeholder="my-new-page"
                data-testid="page-slug"
              />
            </div>
            {validation.slug && <Hint type="error">{validation.slug}</Hint>}
          </div>

          <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4 space-y-3">
            <FieldHeader label="Body" hint="Markdown supported: # heading, **bold**, *italic*, [link](https://…)" ai={{ field: 'page_body', value: form.body, onApply: (t) => setField('body', t), length: 'long', page: 'generic', extraContext: { title: form.title } }}/>
            <textarea
              value={form.body}
              onChange={(e) => setField('body', e.target.value)}
              rows={14}
              className="kc-tinput font-reader text-base leading-relaxed"
              placeholder="Write your page content here. Use Markdown for formatting."
              data-testid="page-body"
            />
            <div className="text-xs text-muted-kc">{form.body.length} characters</div>
          </div>

          <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4 space-y-3">
            <FieldHeader label="Call-to-action button (optional)" hint="Shows at the bottom of the page"/>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-semibold text-muted-kc mb-1">Button label</label>
                <input value={form.cta_label} onChange={(e) => setField('cta_label', e.target.value)} className="kc-tinput" placeholder="Start now" data-testid="page-cta-label"/>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-semibold text-muted-kc mb-1">Button link</label>
                <input value={form.cta_href} onChange={(e) => setField('cta_href', e.target.value)} className="kc-tinput" placeholder="/signup  or  https://..." data-testid="page-cta-href"/>
                {validation.cta_href && <Hint type="error">{validation.cta_href}</Hint>}
              </div>
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4 space-y-3">
            <FieldHeader label="Cover image" hint="Optional. Shows as a hero at the top."/>
            {form.cover_image ? (
              <div className="space-y-2">
                <img src={coverSrc} alt="cover" className="w-full h-40 object-cover rounded-[var(--kc-radius-sm)] border border-kc"/>
                <div className="flex items-center gap-2">
                  <button onClick={() => coverInputRef.current?.click()} className="text-xs font-semibold underline hover:text-[color:var(--kc-gold-2)]">Replace</button>
                  <span className="text-muted-kc/60 text-xs">·</span>
                  <button onClick={() => setField('cover_image', '')} className="text-xs font-semibold text-[color:var(--kc-danger)] underline">Remove</button>
                </div>
              </div>
            ) : (
              <button onClick={() => coverInputRef.current?.click()} className="w-full p-5 border-2 border-dashed border-kc-strong rounded-[var(--kc-radius-md)] text-center hover:bg-surface-2 transition-colors text-sm" data-testid="page-cover-upload">
                {uploading ? <><Loader2 className="mx-auto animate-spin" size={18}/> Uploading…</> : 'Click to upload image'}
              </button>
            )}
            <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files?.[0])}/>
          </div>

          <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4 space-y-3">
            <FieldHeader label="SEO meta" hint="Shown in browser tabs and search results"/>
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-semibold text-muted-kc mb-1">Meta title</label>
              <input value={form.meta_title} onChange={(e) => setField('meta_title', e.target.value)} className="kc-tinput" placeholder={form.title || 'Page title'} data-testid="page-meta-title"/>
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="block text-[10px] uppercase tracking-wider font-semibold text-muted-kc">Meta description</label>
                <AiGenerateButton page="generic" field="page_meta_description" currentValue={form.meta_description || ''} onApply={(t) => setField('meta_description', t)} length="short" extraContext={{ title: form.title, body: (form.body || '').slice(0, 600) }}/>
              </div>
              <textarea value={form.meta_description} onChange={(e) => setField('meta_description', e.target.value)} rows={3} className="kc-tinput" placeholder="One short sentence shown in search results" data-testid="page-meta-desc"/>
              <div className="text-[10px] text-muted-kc mt-1">{(form.meta_description || '').length} / ~160 characters</div>
            </div>
          </div>

          {form.published && form.slug && (
            <div className="bg-gold-soft border border-[color:var(--kc-gold)] rounded-[var(--kc-radius-md)] p-3 text-xs">
              <div className="font-semibold mb-1 inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-[color:var(--kc-gold-2)]"/> Live at:</div>
              <a href={`/p/${form.slug}`} target="_blank" rel="noreferrer" className="font-mono break-all hover:underline">/p/{form.slug}</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MENU MANAGER
   ============================================================ */
function MenuManager() {
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const dragIdRef = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([
        api.get('/admin/menu').then((r) => r.data?.items || []),
        api.get('/admin/pages').then((r) => r.data?.items || []),
      ]);
      setItems(m); setPages(p);
    } catch (e) {
      toast.error('Could not load menu', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreate = () => {
    setEditing({
      id: null, label: '', link_type: 'internal',
      page_slug: '', internal_path: '', external_url: '',
      in_header: true, in_footer: false, open_in_new_tab: false, visible: true,
    });
    setCreating(true);
  };

  const onSave = async (form) => {
    try {
      if (creating || !form.id) {
        await api.post('/admin/menu', form);
        toast.success('Menu item added.');
      } else {
        await api.put(`/admin/menu/${form.id}`, form);
        toast.success('Menu item saved.');
      }
      setEditing(null); setCreating(false);
      await load();
      invalidateMenuCache();
    } catch (e) {
      toast.error('Save failed', { description: String(e?.response?.data?.detail || e.message).slice(0, 220) });
    }
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Delete menu item "${item.label}"?`)) return;
    try {
      await api.delete(`/admin/menu/${item.id}`);
      toast.success('Menu item deleted.');
      await load();
      invalidateMenuCache();
    } catch (e) {
      toast.error('Delete failed', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    }
  };

  const onToggleField = async (item, field) => {
    try {
      await api.put(`/admin/menu/${item.id}`, { [field]: !item[field] });
      await load();
      invalidateMenuCache();
    } catch (e) {
      toast.error('Could not update', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    }
  };

  // ---- Drag & drop reorder ----
  const onDragStart = (e, id) => { dragIdRef.current = id; e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e, id) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverId(id); };
  const onDragLeave = () => setDragOverId(null);
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const sourceId = dragIdRef.current;
    dragIdRef.current = null;
    setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const sIdx = items.findIndex((i) => i.id === sourceId);
    const tIdx = items.findIndex((i) => i.id === targetId);
    if (sIdx < 0 || tIdx < 0) return;
    const reordered = [...items];
    const [moved] = reordered.splice(sIdx, 1);
    reordered.splice(tIdx, 0, moved);
    setItems(reordered);
    try {
      await api.put('/admin/menu/reorder', { ids: reordered.map((i) => i.id) });
      invalidateMenuCache();
      toast.success('Order updated.');
    } catch (e2) {
      toast.error('Reorder failed', { description: String(e2?.response?.data?.detail || e2.message).slice(0, 200) });
      load();
    }
  };

  if (editing) {
    return (
      <MenuItemEditor
        item={editing}
        isNew={creating}
        pages={pages}
        onCancel={() => { setEditing(null); setCreating(false); }}
        onSave={onSave}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-muted-kc">
          {loading ? 'Loading…' : `${items.length} menu item${items.length === 1 ? '' : 's'}`}
        </div>
        <button onClick={onCreate} className="h-10 px-4 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="menu-create">
          <Plus size={16}/> New menu item
        </button>
      </div>

      <div className="bg-gold-soft border border-[color:var(--kc-gold)]/50 rounded-[var(--kc-radius-md)] px-4 py-3 text-xs text-muted-kc flex items-start gap-2">
        <AlertCircle size={14} className="text-[color:var(--kc-gold-2)] mt-0.5 flex-shrink-0"/>
        <div>
          <strong className="text-ink">Custom items appear AFTER your built-in nav</strong> (Courses · Games · Bookshelf · Community · Resources · Support · About).
          The built-in items can’t be removed from here; this section adds your own links on top.
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-muted-kc" size={20}/></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-kc-strong rounded-[var(--kc-radius-md)] bg-surface-2">
          <Link2 size={32} className="mx-auto text-muted-kc mb-3"/>
          <div className="font-display uppercase text-lg mb-1">No menu items yet</div>
          <div className="text-sm text-muted-kc">Click <strong>New menu item</strong> to add a link to your header or footer.</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="menu-list">
          {items.map((item) => (
            <MenuItemRow
              key={item.id}
              item={item}
              dragOver={dragOverId === item.id}
              onDragStart={(e) => onDragStart(e, item.id)}
              onDragOver={(e) => onDragOver(e, item.id)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, item.id)}
              onEdit={() => setEditing(item)}
              onDelete={() => onDelete(item)}
              onToggleField={(f) => onToggleField(item, f)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MenuItemRow({ item, dragOver, onDragStart, onDragOver, onDragLeave, onDrop, onEdit, onDelete, onToggleField }) {
  const typeLabel = item.link_type === 'page' ? 'Custom page' : item.link_type === 'external' ? 'External' : 'Internal route';
  const typeIcon = item.link_type === 'external' ? Globe : item.link_type === 'page' ? FileEdit : Link2;
  const Icon = typeIcon;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`bg-white border rounded-[var(--kc-radius-md)] p-3 flex items-center gap-3 transition-colors ${dragOver ? 'border-[color:var(--kc-gold)] bg-gold-soft' : 'border-kc hover:border-kc-strong'} ${!item.visible ? 'opacity-60' : ''}`}
      data-testid={`menu-row-${item.id}`}
    >
      <button type="button" className="cursor-grab active:cursor-grabbing text-muted-kc hover:text-ink p-1" title="Drag to reorder" aria-label="Drag to reorder">
        <GripVertical size={16}/>
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm">{item.label}</span>
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-surface-2 text-muted-kc px-1.5 py-0.5 rounded-full"><Icon size={10}/> {typeLabel}</span>
          {item.open_in_new_tab && <span className="text-[10px] uppercase tracking-wider font-bold bg-ink/5 text-muted-kc px-1.5 py-0.5 rounded-full">↗ new tab</span>}
        </div>
        <div className="text-xs text-muted-kc font-mono truncate mt-0.5">→ {item.href || '#'}</div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <PillToggle active={item.in_header} onClick={() => onToggleField('in_header')} label="Header" testid={`menu-header-${item.id}`}/>
        <PillToggle active={item.in_footer} onClick={() => onToggleField('in_footer')} label="Footer" testid={`menu-footer-${item.id}`}/>
        <PillToggle active={item.visible} onClick={() => onToggleField('visible')} label={item.visible ? 'Visible' : 'Hidden'} testid={`menu-visible-${item.id}`}/>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={onEdit} className="h-8 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold bg-ink text-white hover:bg-black/90 transition-colors" data-testid={`menu-edit-${item.id}`}>
          <Edit2 size={12}/> Edit
        </button>
        <button onClick={onDelete} className="h-8 px-2 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold text-[color:var(--kc-danger)] hover:bg-red-50 transition-colors" data-testid={`menu-delete-${item.id}`}>
          <Trash2 size={12}/>
        </button>
      </div>
    </div>
  );
}

const PillToggle = ({ active, onClick, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`h-7 px-2.5 inline-flex items-center text-[10px] uppercase tracking-wider font-bold rounded-full border transition-colors ${active ? 'bg-[color:var(--kc-gold)] text-black border-[color:var(--kc-gold)]' : 'bg-white text-muted-kc border-kc hover:border-kc-strong'}`}
  >
    {label}
  </button>
);

function MenuItemEditor({ item, isNew, pages, onCancel, onSave }) {
  const [form, setForm] = useState(item);
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const errors = useMemo(() => {
    const e = {};
    if (!form.label.trim()) e.label = 'Label is required.';
    if (form.link_type === 'page' && !form.page_slug) e.page_slug = 'Pick a page.';
    if (form.link_type === 'internal' && !form.internal_path.trim()) e.internal_path = 'Path is required.';
    if (form.link_type === 'external') {
      const u = form.external_url.trim();
      if (!u) e.external_url = 'URL is required.';
      else if (!u.startsWith('http://') && !u.startsWith('https://')) e.external_url = 'Must start with http:// or https://';
    }
    if (!form.in_header && !form.in_footer) e.placement = 'Show in at least one location (header or footer).';
    return e;
  }, [form]);
  const canSave = Object.keys(errors).length === 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  const publishedPages = pages.filter((p) => p.published);

  return (
    <div className="space-y-4 max-w-2xl" data-testid="menu-item-editor">
      <div className="flex items-center justify-between gap-3 bg-white border border-kc rounded-[var(--kc-radius-md)] p-3 sticky top-2 z-10 shadow-sm">
        <button onClick={onCancel} className="h-9 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm font-semibold text-muted-kc hover:bg-surface-2 transition-colors" data-testid="menu-back">
          <ArrowLeft size={14}/> Back
        </button>
        <button onClick={handleSave} disabled={!canSave || saving} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-testid="menu-save">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} {isNew ? 'Add' : 'Save'}
        </button>
      </div>

      <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4 space-y-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Label <span className="text-[color:var(--kc-danger)]">*</span></label>
          <input value={form.label} onChange={(e) => setField('label', e.target.value)} placeholder="e.g. Podcast" className="kc-tinput" data-testid="menu-label"/>
          {errors.label && <Hint type="error">{errors.label}</Hint>}
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider mb-2">Link type</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'page', label: 'Custom page', icon: FileEdit, hint: 'A page you made' },
              { id: 'internal', label: 'Internal route', icon: Link2, hint: '/games, /support…' },
              { id: 'external', label: 'External URL', icon: Globe, hint: 'opens elsewhere' },
            ].map((opt) => {
              const Ic = opt.icon;
              const active = form.link_type === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setField('link_type', opt.id)}
                  className={`p-3 rounded-[var(--kc-radius-md)] border text-left transition-colors ${active ? 'border-[color:var(--kc-gold)] bg-gold-soft' : 'border-kc hover:border-kc-strong bg-white'}`}
                  data-testid={`menu-type-${opt.id}`}
                >
                  <div className="inline-flex items-center gap-1.5 text-sm font-semibold"><Ic size={14}/> {opt.label}</div>
                  <div className="text-[11px] text-muted-kc mt-0.5">{opt.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        {form.link_type === 'page' && (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Which page?</label>
            <select value={form.page_slug || ''} onChange={(e) => setField('page_slug', e.target.value)} className="kc-tinput" data-testid="menu-page-slug">
              <option value="">— Pick a page —</option>
              {publishedPages.length === 0 && <option value="" disabled>No published pages yet — create one first</option>}
              {publishedPages.map((p) => <option key={p.id} value={p.slug}>{p.title} (/p/{p.slug})</option>)}
            </select>
            {errors.page_slug && <Hint type="error">{errors.page_slug}</Hint>}
            {publishedPages.length === 0 && pages.length > 0 && <Hint type="info">You have drafts — publish one before linking from a menu item.</Hint>}
          </div>
        )}

        {form.link_type === 'internal' && (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Internal path</label>
            <input value={form.internal_path} onChange={(e) => setField('internal_path', e.target.value)} placeholder="/games   or   /resources" className="kc-tinput font-mono" data-testid="menu-internal-path"/>
            {errors.internal_path && <Hint type="error">{errors.internal_path}</Hint>}
            <Hint>Common routes: <code>/courses</code>, <code>/games</code>, <code>/bookshelf</code>, <code>/community</code>, <code>/resources</code>, <code>/support</code>, <code>/about</code>.</Hint>
          </div>
        )}

        {form.link_type === 'external' && (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1">External URL</label>
            <input value={form.external_url} onChange={(e) => setField('external_url', e.target.value)} placeholder="https://example.com" className="kc-tinput" data-testid="menu-external-url"/>
            {errors.external_url && <Hint type="error">{errors.external_url}</Hint>}
            <label className="mt-2 inline-flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.open_in_new_tab} onChange={(e) => setField('open_in_new_tab', e.target.checked)} className="kc-checkbox" data-testid="menu-new-tab"/>
              Open in new tab
            </label>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider mb-2">Show in</label>
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-[var(--kc-radius-sm)] border border-kc cursor-pointer hover:bg-surface-2">
              <input type="checkbox" checked={form.in_header} onChange={(e) => setField('in_header', e.target.checked)} className="kc-checkbox" data-testid="menu-in-header"/>
              <span className="text-sm font-semibold">Header</span>
            </label>
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-[var(--kc-radius-sm)] border border-kc cursor-pointer hover:bg-surface-2">
              <input type="checkbox" checked={form.in_footer} onChange={(e) => setField('in_footer', e.target.checked)} className="kc-checkbox" data-testid="menu-in-footer"/>
              <span className="text-sm font-semibold">Footer</span>
            </label>
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-[var(--kc-radius-sm)] border border-kc cursor-pointer hover:bg-surface-2">
              <input type="checkbox" checked={form.visible} onChange={(e) => setField('visible', e.target.checked)} className="kc-checkbox" data-testid="menu-visible"/>
              <span className="text-sm font-semibold">Visible to visitors</span>
            </label>
          </div>
          {errors.placement && <Hint type="error">{errors.placement}</Hint>}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Small UI helpers
   ============================================================ */
const FieldHeader = ({ label, required, hint, ai }) => (
  <div>
    <div className="flex items-center justify-between gap-2">
      <label className="block text-xs font-semibold uppercase tracking-wider">{label}{required && <span className="text-[color:var(--kc-danger)] ml-1">*</span>}</label>
      {ai && <AiGenerateButton page={ai.page || 'generic'} field={ai.field} currentValue={ai.value || ''} onApply={ai.onApply} length={ai.length || 'medium'} extraContext={ai.extraContext}/>}
    </div>
    {hint && <div className="text-[11px] text-muted-kc mt-0.5">{hint}</div>}
  </div>
);

const Hint = ({ children, type = 'info' }) => {
  const cls = type === 'error' ? 'text-[color:var(--kc-danger)]' : 'text-muted-kc';
  return <div className={`text-[11px] mt-1 ${cls}`}>{children}</div>;
};
